import { TEST_API_PREFIX } from './api-prefix';
import type { RunTraceResult } from './langfuse/types';
import type { TestApiError } from './types';

/**
 * `GET /test/trace` from the page itself.
 *
 * Browser-safe: plain `fetch` against this page's own origin, same as the
 * run route it accompanies. Named distinctly from the server-side
 * `fetchRunTrace` in `langfuse/fetch-run-trace.ts` — that one holds a
 * Langfuse secret key and must never reach this bundle; this one only ever
 * calls `/test/trace`, same-origin, with no credentials of its own.
 */
export class GetRunTraceError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail?: string
  ) {
    super(detail ?? `GET /test/trace failed with ${status}`);
  }
}

export interface GetRunTraceOptions {
  chatId: string;
  /** When the run this trace belongs to started, so the server bounds its
   *  Langfuse query to roughly the right window. */
  sinceMs: number;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}

export const getRunTrace = async ({
  chatId,
  sinceMs,
  signal,
  fetchImpl = fetch,
}: GetRunTraceOptions): Promise<RunTraceResult> => {
  const query = new URLSearchParams({ chatId, sinceMs: String(sinceMs) });
  const response = await fetchImpl(
    `${TEST_API_PREFIX}/trace?${query.toString()}`,
    { signal }
  );

  const body = await response.json().catch(() => null);

  if (!response.ok) {
    const error = body as TestApiError | null;
    throw new GetRunTraceError(response.status, error?.detail ?? error?.error);
  }

  if (body === null) {
    // response.ok but the body didn't parse as JSON — a truncated response or
    // proxy hiccup, not a genuine "no trace found" result. Surfacing it as a
    // valid empty RunTraceResult would be indistinguishable from that case.
    throw new GetRunTraceError(
      response.status,
      'GET /test/trace returned an unreadable response body'
    );
  }

  return body as RunTraceResult;
};
